<script setup lang="ts">
import { computed, onActivated, onDeactivated, onMounted, ref, watch } from 'vue';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import BookmarkButton from '@/components/ui/BookmarkButton.vue';
import OnDemandTag from '@/components/ui/OnDemandTag.vue';
import RouteBadge from '@/components/ui/RouteBadge.vue';
import StopDepartureCards from './StopDepartureCards.vue';
import Common from '@/libs/common';
import { findNearestStop } from '@/libs/helpers/nearby-stop';
import { isCanonicalStop } from '@/libs/helpers/stop-clusters';
import { AppRoute } from '@/libs/routing';
import type { DeviceCount } from '@/@types/device';
import type { Route, Stop, StopTimeData } from '@/@types/gtfs-ressource';
import type { OnDemandRoute } from '@/@types/on-demand-route';
import { onDemandServiceState, type OnDemandServiceState } from '@/libs/on-demand-helper';
import { useAppStore, type LatLong } from '@/stores/app';
import { useClock } from '@/use/clock';
import { useDevicesCountPolling } from '@/use/devices-count-polling';
import { useDevicesStore } from '@/stores/devices';
import { useGtfsStore } from '@/stores/gtfs';
import { useOnDemandStore } from '@/stores/on-demand';

const appStore = useAppStore();
const devicesStore = useDevicesStore();
const gtfsStore = useGtfsStore();
const onDemandStore = useOnDemandStore();

// Shared clock for every departures card, on the stop views' 5s refresh pace.
const clockNow = useClock();

const favoriteRoutes = computed<Route[]>(() => {
  return Object.values(gtfsStore.gtfsData.routes).filter(
    route => appStore.favorites.routes?.[route.route_id] === true,
  );
});

const favoriteOnDemandRoutes = computed<
  Array<{ route: OnDemandRoute; state: OnDemandServiceState; vehicleCount: number }>
>(() =>
  onDemandStore.routes
    .filter(route => appStore.favorites.onDemandRoutes?.[route.route_id] === true)
    .map(route => {
      const vehicleCount = devicesStore.onDemandVehicleCounts.get(route.route_id) ?? 0;
      return {
        route,
        vehicleCount,
        state: onDemandServiceState(route, appStore.todayDateGtfs, vehicleCount),
      };
    }),
);

const favoriteStops = computed<Stop[]>(() => {
  return Object.values(gtfsStore.gtfsData.stops)
    .filter(stop => appStore.favorites.stops?.[stop.stop_id] === true)
    .sort((a, b) => (Common.normalize(a.stop_name) < Common.normalize(b.stop_name) ? -1 : 1));
});

const hasFavorites = computed<boolean>(
  () =>
    favoriteRoutes.value.length > 0 ||
    favoriteOnDemandRoutes.value.length > 0 ||
    favoriteStops.value.length > 0,
);

// #region Shared departures
const sheetStops = computed<Stop[]>(() => {
  const stops = [...favoriteStops.value];
  if (nearestStop.value && !stops.some(stop => stop.stop_id === nearestStop.value?.stop_id)) {
    stops.push(nearestStop.value);
  }
  return stops;
});

const stationIdsByStop = computed<{ [stopId: string]: string[] }>(() => {
  return sheetStops.value.reduce(
    (acc, stop) => {
      acc[stop.stop_id] = gtfsStore.stations[stop.stop_id] || [stop.stop_id];
      return acc;
    },
    {} as { [stopId: string]: string[] },
  );
});

// One stop_times traversal for every card of the sheet, instead of one per
// card: getStopTimes rebuilds its device maps and walks the day's trips on
// each call. Unfiltered on purpose: the builder needs the past departures to
// tell a done-for-today direction ("Terminé") from a stop with no service.
const sheetStopTimes = computed<StopTimeData[]>(() => {
  const allStationIds = Object.values(stationIdsByStop.value).flat();
  if (allStationIds.length === 0) return [];
  return gtfsStore.getStopTimes(allStationIds);
});

function stopTimesFor(stopId: string): StopTimeData[] {
  const stationIds = stationIdsByStop.value[stopId] ?? [stopId];
  return sheetStopTimes.value.filter(st => stationIds.includes(st.stop_id));
}
// #endregion

function getOnlineDeviceCountByRouteId(routeId: string): DeviceCount | undefined {
  return devicesStore.onlineDevicesCount.find(counter => counter.route_id === routeId);
}

// #region Nearest stop
const userPositionGeolocation = computed<LatLong | undefined>(() => appStore.positionGeolocation);
const nearestStop = ref<Stop>();
const showPositionBtn = ref<boolean>(false);

watch(
  () => userPositionGeolocation.value,
  () => {
    if (userPositionGeolocation.value) {
      showPositionBtn.value = false;
      updateNearestStop(userPositionGeolocation.value);
    } else {
      showPositionBtn.value = true;
    }
  },
  { immediate: true },
);

// Recompute once the GTFS stops land (they may arrive after the position).
watch(
  () => gtfsStore.gtfsData.stops,
  () => {
    if (userPositionGeolocation.value) updateNearestStop(userPositionGeolocation.value);
  },
);

function updateNearestStop(userPosition: LatLong) {
  // Only canonical stops: the platform across the street is the same stop, and
  // the canonical one carries the departures of both.
  const candidates = Object.values(gtfsStore.gtfsData.stops).filter(stop =>
    isCanonicalStop(stop.stop_id, gtfsStore.canonicalOf),
  );
  nearestStop.value = findNearestStop(userPosition, candidates);
}

async function requestPosition() {
  await appStore.getGeolocation();
  showPositionBtn.value = !userPositionGeolocation.value;
}
// #endregion

// #region Online devices polling
// The realtime flag of a departure comes from the online devices (getStopTimes
// derives estimated_departure_ts from them), and the map only polls them for a
// selected route/stop/trip: on the home the sheet polls for the routes serving
// its stops. Watched as a sorted key: sheetStopTimes is rebuilt on every poll
// and a fresh array would restart the polling each time.
const sheetRouteIdsKey = computed<string>(() =>
  [...new Set(sheetStopTimes.value.map(st => st.route_id))].filter(Boolean).sort().join(','),
);

function syncDevicesPolling() {
  if (sheetRouteIdsKey.value === '') {
    devicesStore.stopGetDevices();
    return;
  }
  devicesStore.startGetDevices({ routeIds: sheetRouteIdsKey.value.split(',') });
}

watch(sheetRouteIdsKey, syncDevicesPolling);
// #endregion

useDevicesCountPolling();

// A kept-alive tree fires both mounted and activated on its initial mount:
// waking twice would double the devices fetch and the position request.
let awake = false;

// Silent position fetch (no prompt, no popup): when permission was already
// granted, the nearby block shows up without re-tapping the button — nothing
// else requests the position when landing directly on the map.
function onWake() {
  if (awake) return;
  awake = true;
  syncDevicesPolling();
  if (!userPositionGeolocation.value) appStore.getGeolocationIfAllowed();
}

// Leaving the map tree: nothing under the map takes the polling over.
function onSleep() {
  awake = false;
  devicesStore.stopGetDevices();
}

onMounted(onWake);
onActivated(onWake);
onDeactivated(onSleep);
// Nothing to stop on unmount: it means a detail page replaces the home under the
// still-active map, and MapComponent armed its own devices polling before it.
</script>

<template>
  <div class="next-departures">
    <!-- My lines -->
    <section
      v-if="favoriteRoutes.length > 0 || favoriteOnDemandRoutes.length > 0"
      class="next-departures__section"
    >
      <h3 class="next-departures__section-title">{{ $t('home.myRoutes') }}</h3>
      <div class="next-departures__routes">
        <router-link
          v-for="route in favoriteRoutes"
          :key="route.route_id"
          :to="{ name: AppRoute.ROUTE_DETAILED, params: { routeId: route.route_id } }"
          class="next-departures__route-card"
        >
          <RouteBadge :route="route" />
          <div class="next-departures__route-name">
            <span class="next-departures__route-longname">{{ route.route_long_name }}</span>
            <BaseIcon name="chevron-right" class="next-departures__route-chevron" />
          </div>
          <div
            v-if="getOnlineDeviceCountByRouteId(route.route_id)"
            class="next-departures__route-status next-departures__route-status--connected"
          >
            <BaseIcon name="rss" :stroke-width="3" class="next-departures__route-status-icon" />
            {{
              $tc('routes.connectedVehicles', { count: getOnlineDeviceCountByRouteId(route.route_id)?.count })
            }}
          </div>
          <div v-else class="next-departures__route-status">{{ $t('routes.noVehicle') }}</div>
        </router-link>
        <router-link
          v-for="{ route, state, vehicleCount } in favoriteOnDemandRoutes"
          :key="`on-demand:${route.route_id}`"
          :to="{ name: AppRoute.ON_DEMAND_ROUTE_DETAILED, params: { onDemandRouteId: route.route_id } }"
          class="next-departures__route-card"
        >
          <div class="next-departures__route-tags">
            <RouteBadge :route="route" />
            <OnDemandTag />
          </div>
          <div class="next-departures__route-name">
            <span class="next-departures__route-longname">{{ route.route_long_name }}</span>
            <BaseIcon name="chevron-right" class="next-departures__route-chevron" />
          </div>
          <div
            v-if="state === 'live'"
            class="next-departures__route-status next-departures__route-status--connected"
          >
            <BaseIcon name="rss" :stroke-width="3" class="next-departures__route-status-icon" />
            {{ $tc('onDemand.liveVehicles', vehicleCount) }}
          </div>
          <div v-else class="next-departures__route-status">
            {{ state === 'noVehicle' ? $t('onDemand.noVehicle') : $t('onDemand.noServiceToday') }}
          </div>
        </router-link>
      </div>
    </section>

    <!-- My stops -->
    <section v-if="favoriteStops.length > 0" class="next-departures__section">
      <h3 class="next-departures__section-title">{{ $t('home.myStops') }}</h3>
      <div v-for="stop in favoriteStops" :key="stop.stop_id" class="next-departures__stop">
        <div class="next-departures__stop-header">
          <span class="next-departures__stop-name">{{ stop.stop_name }}</span>
          <BookmarkButton :element="stop" />
        </div>
        <StopDepartureCards
          :stop-id="stop.stop_id"
          :stop-times="stopTimesFor(stop.stop_id)"
          :now="clockNow"
        />
      </div>
    </section>

    <!-- Stops near you -->
    <section v-if="nearestStop" class="next-departures__section">
      <h3 class="next-departures__section-title">{{ $t('home.nearbyStops') }}</h3>
      <div class="next-departures__stop">
        <div class="next-departures__stop-header">
          <span class="next-departures__stop-name">{{ nearestStop.stop_name }}</span>
        </div>
        <StopDepartureCards
          :stop-id="nearestStop.stop_id"
          :stop-times="stopTimesFor(nearestStop.stop_id)"
          :now="clockNow"
        />
      </div>
    </section>
    <button v-else-if="showPositionBtn" class="next-departures__position-btn" @click="requestPosition">
      <BaseIcon name="map-pin" class="next-departures__position-btn-icon" />
      {{ $t('stops.seeNearbyStops') }}
    </button>

    <!-- Favorites empty state -->
    <div v-if="!hasFavorites" class="next-departures__empty">
      <BaseIcon name="bookmark" class="next-departures__empty-icon" />
      <div class="next-departures__empty-title">{{ $t('home.favorites.empty.title') }}</div>
      <div class="next-departures__empty-subtitle">{{ $t('home.favorites.empty.subtitle') }}</div>
    </div>
  </div>
</template>

<style lang="scss">
.next-departures {
  background-color: $color-neutral-lower;
  display: flex;
  flex-direction: column;
  gap: $spacing-5;
  min-height: 100%;
  padding: $spacing-4 $spacing-5 $spacing-5;

  &__section {
    display: flex;
    flex-direction: column;
    gap: $spacing-3;
  }

  &__section-title {
    color: $color-neutral-highest;
    font: $text-bold-large;
    letter-spacing: $text-bold-large-spacing;
    margin: 0;
  }

  &__routes {
    display: flex;
    gap: $spacing-3;
    margin: 0 (-$spacing-5);
    overflow-x: auto;
    padding: 0 $spacing-5 $spacing-1;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__route-card {
    background-color: $color-white;
    border-radius: $radius-3xl;
    border: 1px solid $color-border-medium;
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    gap: $spacing-2;
    max-width: 280px;
    min-width: 220px;
    padding: $spacing-4;

    &:active {
      background-color: $color-neutral-lower;
    }
  }

  &__route-tags {
    align-items: center;
    display: flex;
    gap: $spacing-2;
  }

  &__route-name {
    align-items: center;
    display: flex;
    gap: $spacing-2;
  }

  &__route-longname {
    color: $color-text-dark;
    flex: 1;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__route-chevron {
    color: $color-neutral-higher;
    flex-shrink: 0;
    font-size: 16px;
  }

  &__route-status {
    align-items: center;
    color: $color-neutral-higher;
    display: flex;
    font: $label-smaller;
    gap: 6px;
    letter-spacing: $label-smaller-spacing;

    &--connected .next-departures__route-status-icon {
      color: $color-primary;
      font-size: 16px;
      transform: rotate(270deg);
    }
  }

  &__stop {
    display: flex;
    flex-direction: column;
    gap: $spacing-2;
  }

  &__stop-header {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  &__stop-name {
    color: $color-text-dark;
    font: $text-bold-medium;
    letter-spacing: $text-bold-medium-spacing;
    text-transform: uppercase;
  }

  &__position-btn {
    align-items: center;
    background-color: $color-white;
    border: none;
    border-radius: $radius-pill;
    box-shadow: $shadow-soft;
    color: $color-neutral-highest;
    cursor: pointer;
    display: flex;
    font: $label-medium;
    gap: $spacing-2;
    justify-content: center;
    padding: $spacing-3 $spacing-4;
  }

  &__position-btn-icon {
    font-size: 20px;
  }

  &__empty {
    align-items: center;
    display: flex;
    flex-direction: column;
    padding: $spacing-6 $spacing-5;
    text-align: center;
  }

  &__empty-icon {
    align-items: center;
    display: flex;
    font-size: 24px;
    justify-content: center;
    fill: $color-primary-highter;
    color: $color-primary-highter;
  }

  &__empty-title {
    color: $color-neutral-highest;
    font: $text-bold-medium;
    letter-spacing: $text-bold-medium-spacing;
    margin-top: $spacing-1;
  }

  &__empty-subtitle {
    color: $color-neutral-higher;
    font: $text-small;
    letter-spacing: $text-small-spacing;
    margin-top: $spacing-1;
    max-width: 280px;
  }
}
</style>
